class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.addEventListener("click", this.onDrawerClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) {
        overlay.removeAttribute("style");
        overlay.style.display = "";
        overlay.style.opacity = "";
        overlay.style.visibility = "";
      }
      this.classList.add("animate", "active", "is-active");
      // Notify scripts (e.g. drag-scroll init) that the drawer is now visible in the DOM
      this.dispatchEvent(new CustomEvent('cart-drawer:opened', { bubbles: true }));
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active", "is-active", "animate");
    
    // Overlay'i kesinlikle temizle
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.style.display = "none";
      overlay.style.opacity = "0";
      overlay.style.visibility = "hidden";
    }
    
    // Tüm olası overlay'leri de temizle
    document.querySelectorAll('.cart-drawer__overlay').forEach(el => {
      el.style.display = "none";
      el.style.opacity = "0";
      el.style.visibility = "hidden";
    });
    
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  async renderContents(parsedState, openDrawerMenu=true) {
    if (this.querySelector(".drawer__inner")) {
      this.querySelector(".drawer__inner").classList.contains("is-empty") &&
        this.querySelector(".drawer__inner").classList.remove("is-empty");
    }
    this.productId = parsedState.id;

    if (!parsedState.sections) {
      parsedState.sections = await fetch(
        `/?sections=${this.getSectionsToRender().map((section) => section.id)}`
      ).then((t) => t.json());
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      if (!this.querySelector("#CartDrawer-Overlay")) return;
      
      // Overlay event listener'ı yeniden ekle
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) {
        // Eski listener'ları temizle
        const newOverlay = overlay.cloneNode(true);
        overlay.parentNode.replaceChild(newOverlay, overlay);
        
        // Yeni listener ekle
        newOverlay.addEventListener("click", this.close.bind(this));
      }
      
      if (openDrawerMenu) {
        this.open(); // open() dispatches cart-drawer:opened internally
      } else {
        // Drawer stays open but DOM was re-rendered — reinitialize components
        // (e.g. drag-scroll) on the fresh slider nodes
        this.dispatchEvent(new CustomEvent('cart-drawer:opened', { bubbles: true }));
      }
    });
  }

  onDrawerClick(event) {
    // Also handles .rec-add-button in the inline recommended-products slider
    const addButton = event.target.closest(".cart-recommendation-card__add-button, .rec-add-button");
    if (!addButton) return;

    const variantId = addButton.dataset.variantId;
    if (!variantId) return;

    this.addRecommendationToCart(addButton, variantId);
  }

  addRecommendationToCart(button, variantId) {
    button.disabled = true;
    button.classList.add("loading");

    const payload = {
      id: Number(variantId),
      quantity: 1,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          throw new Error(data.description || "Add to cart failed");
        }
        // Brief "added" feedback on the button before the drawer re-renders
        button.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';
        button.classList.add("rec-add-button--added");
        setTimeout(() => this.renderContents(data, false), 400);
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);
      })
      .finally(() => {
        button.disabled = false;
        button.classList.remove("loading");
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return;
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)
      .innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Global overlay temizleyici - son çare
document.addEventListener('DOMContentLoaded', () => {
  // Cart drawer kapandığında tüm overlay'leri temizle
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && !cartDrawer.classList.contains('active') && !cartDrawer.classList.contains('is-active')) {
          document.querySelectorAll('.cart-drawer__overlay, #CartDrawer-Overlay').forEach(overlay => {
            overlay.style.display = 'none';
            overlay.style.opacity = '0';
            overlay.style.visibility = 'hidden';
          });
        }
      }
    });
  });

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    observer.observe(cartDrawer, { attributes: true });
  }
});